import { RegistryProvider } from '@effect/atom-react';
import { Toaster } from '@listening/frontend/components/radix/sonner';
import { TooltipProvider } from '@listening/frontend/components/radix/tooltip';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { AnalyticsDevtoolsPanel } from '@listening/frontend/analytics/tanstack-devtools-plugin';
import { AppRuntimeProvider } from '@listening/frontend/query/runtime-context';
import type React from 'react';
import { useIsClient } from 'usehooks-ts';
import { useTrackingCapture } from '@/hooks/use-tracking-capture';
import { clientEnv, isDev } from '@/env';
import { getRuntime } from '@/runtimes/get-runtime';

// Must render INSIDE RegistryProvider: the capture writes the tracking-params
// atom, and the checkout form reads it from the app registry — a capture run
// against the module-global default registry would be invisible to checkout.
function TrackingCaptureBridge() {
  useTrackingCapture();
  return null;
}

export function Providers({ children }: React.PropsWithChildren) {
  // Read after mount: the server render has no navigator to agree with.
  const isAutomatedBrowser = useIsClient() && navigator.webdriver;
  return (
    // AppRuntimeProvider hands the shared `@listening/frontend/query`
    // hooks (eq.queryOptions / useOptimisticLifecycle / createListEntity) the
    // start app's runtime. `getRuntime()` is isomorphic — server runtime
    // during SSR, client runtime in the browser.
    <AppRuntimeProvider runtime={getRuntime()}>
      {/* RegistryProvider scopes effect-atom state per React tree — one
          registry per SSR request, one per browser tab — instead of the
          module-global default registry (which would leak state across SSR
          requests). Part of the zustand→atom off-ramp. */}
      <RegistryProvider>
        <TrackingCaptureBridge />
        <TooltipProvider delayDuration={300}>
          <DevToolsProvider
            enabled={
              isDev && clientEnv.mode !== 'production' && !isAutomatedBrowser
            }
          >
            {children}
          </DevToolsProvider>
          <Toaster />
        </TooltipProvider>
      </RegistryProvider>
    </AppRuntimeProvider>
  );
}

function DevToolsProvider({
  children,
  ...opts
}: {
  children: React.ReactNode;
  enabled?: boolean;
}) {
  const enabled = opts.enabled ?? true;

  return (
    <>
      {children}
      {enabled && (
        <TanStackDevtools
          plugins={[
            {
              name: 'TanStack Query',
              render: <ReactQueryDevtoolsPanel />,
            },
            {
              name: 'TanStack Router',
              render: <TanStackRouterDevtoolsPanel />,
            },
            {
              name: 'Analytics & Flags',
              render: <AnalyticsDevtoolsPanel />,
            },
            // {
            //   name: 'TanStack Form',
            //   render: <ReactFormDevtoolsPanel />,
            // },
          ]}
        />
      )}
    </>
  );
}
